/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { JWTData } from 'libs/http/api/dashboard/dashboard.types';
import { dashboard } from 'libs/http/api/dashboard/dashboard';
import { getCookie, getDomainName, setCookie } from 'libs/node';
import { getNextTwoWeek } from 'libs/date';
import { isNotBusinessEmail } from 'libs/string';

import { useInt } from 'hooks/useInt';
import { useRequest } from 'hooks/useRequest';

import { RegisChat } from 'ui/molecules/RegisChat/RegisChat';

import { authConfig } from 'features/agents/helpers/utils';

import { getRegisMessages } from './PepsSanctionsPage.data';

type Step = 'prompt' | 'email' | 'code';

const formatError = (value: string | string[] | undefined): string => {
  if (!value) {
    return '';
  }

  return Array.isArray(value) ? value.join(', ') : value;
};

export const RegisPrompt = () => {
  const { t } = useInt();
  const [step, setStep] = useState<Step>('prompt');
  const [prompt, setPrompt] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const redirectTimer = useRef<NodeJS.Timeout>(undefined);

  useEffect(() => () => clearTimeout(redirectTimer.current as NodeJS.Timeout), []);

  const { request: requestRegister, loading: registerLoading, errors: registerErrors } = useRequest<void>();
  const { request: requestVerify, loading: verifyLoading, errors: verifyErrors } = useRequest<JWTData>();

  const loading = registerLoading || verifyLoading;
  const errors = step === 'code' ? verifyErrors : registerErrors;

  const errorMessage =
    emailError ||
    formatError(errors.detail as string | string[]) ||
    formatError(errors.non_field_errors as string | string[]) ||
    formatError(errors.email as string | string[]) ||
    formatError(errors.code as string | string[]);

  const redirectToAgent = useCallback((promptValue: string) => {
    window.location.href = `${process.env.NEXT_PUBLIC_PLATFORM}/app-esg/esg/agent?prompt=${encodeURIComponent(promptValue)}`;
  }, []);

  const handleBack = useCallback(() => {
    setStep((current) => (current === 'code' ? 'email' : 'prompt'));
  }, []);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    setEmailError('');
  }, []);

  const onPromptSubmit = useCallback(() => {
    if (!prompt.trim()) {
      return;
    }

    if (getCookie(authConfig.ACCESS_KEY)) {
      redirectToAgent(prompt);

      return;
    }

    setStep('email');
  }, [prompt, redirectToAgent]);

  const onEmailSubmit = useCallback(async () => {
    if (!email.trim() || loading) {
      return;
    }

    if (isNotBusinessEmail(email)) {
      setEmailError(t('Please enter your business email address'));

      return;
    }

    setEmailError('');

    try {
      await requestRegister(dashboard.auth.registerRegis({ email }));
      setStep('code');
    } catch (err: any) {
      // 409: user already registered — notify, then redirect to their app with the prompt
      if (err?.code === 'application_already_added' && err?.redirect) {
        const separator = err.redirect.includes('?') ? '&' : '?';
        const target = `${err.redirect}${separator}prompt=${encodeURIComponent(prompt)}`;
        setSuccessMessage(t('It looks like you already have an account. We will take you now to the login page…'));
        redirectTimer.current = setTimeout(() => {
          window.location.href = target;
        }, 3000);
      }
    }
  }, [email, loading, prompt, requestRegister, t]);

  const onCodeSubmit = useCallback(async () => {
    if (!code.trim() || loading) {
      return;
    }

    try {
      const { access, refresh } = await requestVerify(dashboard.auth.verify({ email, code }));
      if (!access || !refresh) {
        return;
      }

      const cookieOptions = {
        expires: getNextTwoWeek(),
        path: '/',
        domain: getDomainName(window.location.hostname),
      };
      setCookie(authConfig.ACCESS_KEY, access, cookieOptions);
      setCookie(authConfig.REFRESH_KEY, refresh, cookieOptions);

      redirectToAgent(prompt);
    } catch {
      // errors surfaced via `errors` state from useRequest
    }
  }, [code, email, loading, prompt, redirectToAgent, requestVerify]);

  const chatProps = useMemo(() => {
    if (step === 'email') {
      return {
        value: email,
        onValueChange: handleEmailChange,
        onSubmit: onEmailSubmit,
        placeholder: t('Enter your business email'),
        submitLabel: loading ? t('Sending…') : t('Send code'),
        inputType: 'email' as const,
        showBack: true,
        onBack: handleBack,
        submitDisabled: !email.trim(),
      };
    }

    if (step === 'code') {
      return {
        value: code,
        onValueChange: setCode,
        onSubmit: onCodeSubmit,
        placeholder: t('Enter verification code'),
        submitLabel: loading ? t('Verifying…') : t('Verify'),
        inputType: 'text' as const,
        showBack: true,
        onBack: handleBack,
        submitDisabled: !code.trim(),
      };
    }

    return {
      value: prompt,
      onValueChange: setPrompt,
      onSubmit: onPromptSubmit,
      placeholder: t('Ask Regis: screen any company, its directors or UBOs…'),
      submitLabel: t('Ask →'),
      inputType: 'text' as const,
      submitDisabled: !prompt.trim(),
    };
  }, [step, prompt, email, code, loading, handleEmailChange, onEmailSubmit, onCodeSubmit, onPromptSubmit, handleBack, t]);

  const note =
    successMessage ||
    (step === 'code' ? (
      <>
        {t('Code sent to')} <strong>{email}</strong>. {t('Check your inbox.')}
      </>
    ) : undefined);

  const error = successMessage || errorMessage === 'application_already_added' ? '' : errorMessage;

  return (
    <RegisChat
      messages={getRegisMessages(t)}
      status={t('Connected to registry data')}
      loading={loading}
      error={error}
      note={note}
      {...chatProps}
    />
  );
};
